// ── WhatsAppQualityPage ────────────────────────────────────────────
//
// Quality monitoring dashboard for WhatsApp Business accounts.
// Shows current quality ratings, tier limits, and the impact of
// rating changes on broadcasts.
//
// Data sources:
//   GET /whatsapp/accounts — accounts with qualityRating + status
//   GET /whatsapp/broadcasts?status=PAUSED — paused by quality

import { useCallback, useEffect, useState } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  ExternalLink,
  Loader2,
  RefreshCw,
  Shield,
  TrendingUp,
  XCircle,
  Zap,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { toast } from '@/components/ui/toast';
import { listWhatsappAccounts, listBroadcasts, type WhatsappAccountListItem, type BroadcastRow } from '../api/whatsapp.api';

// ── Quality tier configuration ─────────────────────────────────────

const QUALITY_CONFIG = {
  GREEN: {
    label: 'High Quality',
    description: 'You can increase your messaging tier limits.',
    icon: CheckCircle2,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    badgeClass: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  },
  YELLOW: {
    label: 'Medium Quality',
    description: 'Warning: quality has degraded. Reduce opt-outs to avoid Red status.',
    icon: AlertTriangle,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    badgeClass: 'bg-amber-50 text-amber-700 border-amber-200',
  },
  RED: {
    label: 'Low Quality',
    description: 'Critical: marketing broadcasts auto-paused. Improve quality to resume.',
    icon: XCircle,
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    badgeClass: 'bg-red-50 text-red-700 border-red-200',
  },
  UNKNOWN: {
    label: 'Unknown',
    description: 'Quality rating not yet synced from Meta.',
    icon: AlertCircle,
    color: 'text-slate-500',
    bgColor: 'bg-slate-50',
    borderColor: 'border-slate-200',
    badgeClass: 'bg-slate-100 text-slate-500 border-slate-200',
  },
};

// ── Quality tips ───────────────────────────────────────────────────

const QUALITY_TIPS = [
  {
    icon: Shield,
    title: 'Honour opt-outs immediately',
    desc: 'Process STOP / unsubscribe keywords in under 24h. BrixLead does this automatically.',
  },
  {
    icon: Zap,
    title: 'Send only opted-in contacts',
    desc: 'Use the Compliance page to verify your contact list before every broadcast.',
  },
  {
    icon: TrendingUp,
    title: 'Keep message frequency low',
    desc: 'Avoid sending more than 1-2 messages per day. High frequency correlates with block rates.',
  },
  {
    icon: CheckCircle2,
    title: 'Use utility templates for transactional messages',
    desc: 'Order confirmations, OTPs, and delivery updates should use UTILITY category — lower cost and higher delivery rates.',
  },
  {
    icon: AlertCircle,
    title: 'Review rejected templates',
    desc: 'Templates rejected by Meta often contain promotional language that triggers quality flags.',
  },
];

// ── Account quality card ───────────────────────────────────────────

function AccountQualityCard({
  account,
  pausedBroadcasts,
}: {
  account: WhatsappAccountListItem;
  pausedBroadcasts: BroadcastRow[];
}) {
  const navigate = useNavigate();
  const q = account.qualityRating as keyof typeof QUALITY_CONFIG;
  const cfg = QUALITY_CONFIG[q] ?? QUALITY_CONFIG.UNKNOWN;
  const Icon = cfg.icon;
  const accountPaused = pausedBroadcasts.filter(
    (b) => b.whatsappAccountId === account.id,
  );

  return (
    <div
      className={`rounded-xl border p-5 ${cfg.bgColor} ${cfg.borderColor}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className={`flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-sm`}>
            <Icon className={`h-5 w-5 ${cfg.color}`} />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-800">
              {account.verifiedName ?? account.displayPhoneNumber}
            </p>
            <p className="text-xs text-slate-500">{account.displayPhoneNumber}</p>
          </div>
        </div>
        <span
          className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${cfg.badgeClass}`}
        >
          {cfg.label}
        </span>
      </div>

      <p className={`mt-3 text-sm ${cfg.color} font-medium`}>{cfg.description}</p>

      {accountPaused.length > 0 && (
        <div className="mt-3 rounded-lg bg-white p-3 shadow-sm">
          <p className="mb-2 text-xs font-semibold text-red-700">
            {accountPaused.length} broadcast{accountPaused.length !== 1 ? 's' : ''} auto-paused
          </p>
          <div className="space-y-1">
            {accountPaused.slice(0, 3).map((b) => (
              <button
                key={b.id}
                onClick={() => navigate(`/whatsapp/broadcasts/${b.id}`)}
                className="flex w-full items-center justify-between rounded-md px-2 py-1 text-xs hover:bg-red-50"
              >
                <span className="text-slate-700">{b.name}</span>
                <ChevronRight className="h-3 w-3 text-slate-400" />
              </button>
            ))}
            {accountPaused.length > 3 && (
              <button
                onClick={() => navigate('/whatsapp/broadcasts?status=PAUSED')}
                className="text-xs text-red-600 hover:underline"
              >
                +{accountPaused.length - 3} more
              </button>
            )}
          </div>
        </div>
      )}

      {q === 'GREEN' && (
        <div className="mt-3 rounded-lg bg-white p-3 shadow-sm">
          <p className="text-xs text-emerald-700">
            Maintain this rating and Meta will allow you to increase your daily message limit.
          </p>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────

export default function WhatsAppQualityPage() {
  const [accounts, setAccounts] = useState<WhatsappAccountListItem[]>([]);
  const [pausedBroadcasts, setPausedBroadcasts] = useState<BroadcastRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [accs, paused] = await Promise.all([
        listWhatsappAccounts(),
        listBroadcasts({ status: 'PAUSED', limit: 50 }),
      ]);
      setAccounts(accs);
      setPausedBroadcasts(paused.data);
    } catch {
      toast('error', 'Failed to load quality data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const redAccounts = accounts.filter((a) => a.qualityRating === 'RED');
  const yellowAccounts = accounts.filter((a) => a.qualityRating === 'YELLOW');

  return (
    <DashboardLayout title="WhatsApp Quality">
      <div className="mx-auto max-w-4xl space-y-6 px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-800">Quality Monitor</h1>
            <p className="text-sm text-slate-500">
              Meta rates your number's quality based on user feedback (blocks, reports, opt-outs).
            </p>
          </div>
          <button
            onClick={load}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>

        {/* Alert banners */}
        {!loading && redAccounts.length > 0 && (
          <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4">
            <XCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
            <div>
              <p className="font-semibold text-red-800">
                {redAccounts.length} account{redAccounts.length > 1 ? 's' : ''} in RED quality
              </p>
              <p className="mt-0.5 text-sm text-red-700">
                Marketing broadcasts have been auto-paused. Improve your quality score by reducing
                opt-outs and block rates before resuming.
              </p>
            </div>
          </div>
        )}

        {!loading && yellowAccounts.length > 0 && redAccounts.length === 0 && (
          <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
            <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" />
            <div>
              <p className="font-semibold text-amber-800">Quality warning</p>
              <p className="mt-0.5 text-sm text-amber-700">
                {yellowAccounts.length} account{yellowAccounts.length > 1 ? 's have' : ' has'} degraded to YELLOW.
                If this continues, marketing broadcasts may be paused automatically.
              </p>
            </div>
          </div>
        )}

        {/* Account quality cards */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
          </div>
        ) : accounts.length === 0 ? (
          <div className="rounded-xl border-2 border-dashed border-gray-200 py-16 text-center">
            <p className="text-sm text-slate-400">
              No WhatsApp accounts connected.{' '}
              <a href="/whatsapp/settings" className="text-indigo-600 hover:underline">
                Connect one
              </a>
              .
            </p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {accounts.map((a) => (
              <AccountQualityCard
                key={a.id}
                account={a}
                pausedBroadcasts={pausedBroadcasts}
              />
            ))}
          </div>
        )}

        {/* Quality tier reference */}
        <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-sm font-semibold text-slate-700">Quality Rating Tiers</h2>
          <div className="space-y-3">
            {(['GREEN', 'YELLOW', 'RED'] as const).map((q) => {
              const cfg = QUALITY_CONFIG[q];
              const Icon = cfg.icon;
              return (
                <div key={q} className="flex items-start gap-3">
                  <div className={`mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full ${cfg.bgColor}`}>
                    <Icon className={`h-4 w-4 ${cfg.color}`} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-700">{cfg.label}</p>
                    <p className="text-xs text-slate-500">{cfg.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <a
            href="https://developers.facebook.com/docs/whatsapp/overview/phone-number-quality-rating"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline"
          >
            Read Meta documentation <ExternalLink className="h-3 w-3" />
          </a>
        </div>

        {/* Tips to improve quality */}
        <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-sm font-semibold text-slate-700">Tips to Improve Quality</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {QUALITY_TIPS.map((tip) => {
              const Icon = tip.icon;
              return (
                <div key={tip.title} className="flex items-start gap-3 rounded-lg bg-slate-50 p-3">
                  <Icon className="mt-0.5 h-4 w-4 flex-shrink-0 text-indigo-500" />
                  <div>
                    <p className="text-xs font-semibold text-slate-700">{tip.title}</p>
                    <p className="mt-0.5 text-[11px] text-slate-500">{tip.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
